import { useEffect, useState, useRef, ReactNode } from 'react';
import { ChannelFeedChannel } from './interfaces';
import styles from './channelGuide.module.scss';
import { getChannelImageLoadingProps, hiddenChannelImageLoadingProps, isLiveWithTimeLeft } from './utils';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { ChannelCardStrip } from '@/ui-components/molecules/ChannelCardStrip/ChannelCardStrip';
import { ChannelBadge } from '@/ui-components/molecules/ChannelCardStrip/ChannelBadge';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { PLATFORM } from '@/constants';
import { resolveChannelDescription } from './channelDescription';

const { channelList, channelCardStripWrapper, xlDescription, badgeStyles, explicitContainerWrapper } = styles;

const INITIAL_BATCH_COUNT = 15;
const NEXT_BATCH_COUNT = 15;
const SCROLL_LOAD_AHEAD_PX = 600;

interface Props {
    channels?: ChannelFeedChannel[] | null;
    upsellLink?: SiteLink;
    display_listen_live?: boolean;
    platform?: string;
    defaultChannelImage: {
        url: string;
        altText: string;
    };
    children?: ReactNode;
    removeFirstBatch?: boolean;
    renderedAt: number;
}

interface VisibleChannelState {
    channels?: ChannelFeedChannel[] | null;
    visibleCount: number;
}

export default function ChannelList({
    channels,
    upsellLink,
    platform,
    defaultChannelImage,
    children,
    removeFirstBatch,
    renderedAt,
}: Props) {
    const triggerElementReference = useRef<HTMLDivElement>(null);
    const [visibleChannelState, setVisibleChannelState] = useState<VisibleChannelState>({
        channels,
        visibleCount: INITIAL_BATCH_COUNT,
    });
    const visibleCount =
        visibleChannelState.channels === channels ? visibleChannelState.visibleCount : INITIAL_BATCH_COUNT;
    const channelListStrips = channels?.slice(0, visibleCount);

    useEffect(() => {
        if (channels) {
            const onScroll = () => {
                if (triggerElementReference.current) {
                    const rect = triggerElementReference.current.getBoundingClientRect();

                    if (rect.top < window.innerHeight + SCROLL_LOAD_AHEAD_PX && rect.bottom >= 0) {
                        setVisibleChannelState((prevState) => {
                            const nextVisibleCount =
                                prevState.channels === channels ? prevState.visibleCount : INITIAL_BATCH_COUNT;

                            if (nextVisibleCount >= channels.length) {
                                return prevState;
                            }
                            return {
                                channels,
                                visibleCount: Math.min(nextVisibleCount + NEXT_BATCH_COUNT, channels.length),
                            };
                        });
                    }
                }
            };

            if (typeof window !== 'undefined') {
                window.scrollTo(0, 0);
            }

            window.addEventListener('scroll', onScroll);

            return () => window.removeEventListener('scroll', onScroll);
        }

        return undefined;
    }, [channels]);

    const cardStripChannelData = channelListStrips?.map((channel, index) => {
        let listenLink = channel.deepLink || '';
        let listenAria = `listen live to ${channel.displayName}`;
        const airNowAria = `On Air Now ${channel.displayName}`;

        if (!channel.availableToPackage && upsellLink) {
            listenLink = upsellLink.fields.href || '';
            listenAria = 'upgrade plan';
        }

        const subTitleDetails = resolveChannelDescription(channel);

        let channelNumber = <>{platform === PLATFORM.SIRIUS ? channel.siriusChannelNumber : channel.xmChannelNumber}</>;
        if (!(channel.deliveryTypes ?? []).includes('satellite')) {
            channelNumber = <></>;
        }

        let showLogo;
        let showTitle;
        const { isLive, timeLeft, liveShowID } = isLiveWithTimeLeft(channel?.showSchedules?.schedules, renderedAt);

        channel?.showSchedules?.shows?.forEach((show) => {
            if (show.showId === liveShowID) {
                showLogo = show.showLogo;
                showTitle = show.showName;
            }
        });

        let playIcon = true;
        if ((channel.deliveryTypes?.length ?? 0) === 1 && channel.deliveryTypes?.[0] === 'satellite') {
            playIcon = false;
        }

        return {
            title: channel.displayName,
            live: isLive,
            subTitle: channel.channel_type === 'Xtra' ? undefined : channel.shortDescription,
            subTitleDetails: subTitleDetails,
            showAndScheduleLink: channel.vanityURL ? `${channel.vanityURL}#web2-channel-guide` : '',
            showsAndScheduleLinkAriaLabel: `shows and schedules for ${channel.displayName}`,
            listenLink: listenLink,
            listenLinkAriaLabel: listenAria,
            onAirNowArialLabel: airNowAria,
            showAndScheduleLinkText: 'Show information & Schedules',
            locked: !channel.availableToPackage,
            onAirTime: !channel.xtra_channel ? timeLeft : '',
            onAirImage: (
                <ImageWrapper
                    src={showLogo || channel?.web_2_0_image?.url || defaultChannelImage?.url}
                    optimizedHeight={128}
                    optimizedWidth={128}
                    optimizedMobileHeight={128}
                    optimizedMobileWidth={128}
                    {...hiddenChannelImageLoadingProps}
                />
            ),
            showName: showTitle || channel.displayName,
            channelImage: (
                <ImageWrapper
                    src={channel?.web_2_0_image?.url || defaultChannelImage?.url}
                    alt={channel?.web_2_0_image?.altText || defaultChannelImage?.altText}
                    optimizedHeight={128}
                    optimizedWidth={128}
                    optimizedMobileHeight={128}
                    optimizedMobileWidth={128}
                    {...getChannelImageLoadingProps(index)}
                />
            ),
            plansLink: 'https://www.siriusxm.com/plans',
            plansLinkAriaLabel: 'upgrade plan',
            titleLink: channel.vanityURL || '',
            titleAriaLabel: 'channels',
            channelNumber: channelNumber,
            id: channel.assetId,
            bannerText: channel.channelNotification,
            isXLBadge: channel.explicitContentIndicator === 'Yes',
            isOnlyOnBadge: channel.exclusiveContent,
            isAppOnlyBadge: !(channel.deliveryTypes ?? []).includes('satellite'),
            playIcon: playIcon,
        };
    });

    return (
        <Grid container>
            <Grid col={{ sm: 12 }}>
                <div className={channelList}>
                    {
                        <>
                            {!removeFirstBatch && <>{children}</>}
                            {cardStripChannelData?.length &&
                                cardStripChannelData.map((channelData, index) => {
                                    if (!removeFirstBatch && index <= 9) {
                                        return;
                                    }
                                    return (
                                        <div key={channelData.id} className={channelCardStripWrapper}>
                                            <ChannelCardStrip {...channelData} />
                                        </div>
                                    );
                                })}
                            {channelListStrips && channelListStrips.length < (channels?.length || 0) && (
                                <div ref={triggerElementReference} />
                            )}
                            {cardStripChannelData && cardStripChannelData.length > 0 && (
                                <div className={explicitContainerWrapper}>
                                    <ChannelBadge badgeText="Explicit" className={badgeStyles} />
                                    <p className={xlDescription}>
                                        May include frequent explicit language or mature programming. Call SiriusXM
                                        Listener Care at 1-800-967-2346 and ask about Family Friendly plans. All
                                        programming subject to change.
                                    </p>
                                </div>
                            )}
                        </>
                    }
                </div>
            </Grid>
        </Grid>
    );
}
